import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiBaseUrl,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  BaseData,
  LegalEntity,
  PaymentProcessor,
} from '@pflegenavi/shared/api';

export interface TenantInformation {
  name: string;
  payment_processor: PaymentProcessor;
  payment_processor_mode: string;
  allow_deletion: boolean;
  receipt_images_required: boolean;
  inserted_at: Date;
  updated_at: Date;
}

export interface NursingHomeLegalEntity {
  id: string;
  mangopay_sca_enrolled: boolean;
  legal_person_type: 'BUSINESS' | 'ORGANIZATION' | 'SOLETRADER' | 'PARTNERSHIP';
  mandatory_cash_link_for_receipts: boolean;
}

export interface NursingHome {
  id: string;
  name: string;
  feature_flags: string[];
  min_payment_amount: number;
  max_payment_amount: number;
  min_payment_amount_employee_initiated: number;
  max_payment_amount_employee_initiated: number;
  in_settlement_min_payment_amount: number;
  min_resident_thresholds_value: number;
  max_resident_thresholds_value: number;
  payment_disabled: boolean;
  legal_entity_id: string;
  pdf_logo_id: string;
  legal_entity: NursingHomeLegalEntity | null;
}

export interface NursingHomeUpdate {
  name: string;
}

export interface INursingHomePhoenixApi extends Api {
  getTenantInformation(): Promise<{
    data: TenantInformation;
  }>;

  getNursingHomes(): Promise<{
    meta: {
      total_count: number;
    };
    data: NursingHome[];
  }>;

  getNursingHome(dataIn: {
    params: {
      nursingHomeId: string;
    };
  }): Promise<NursingHome>;

  updateNursingHome(dataIn: {
    params: {
      nursingHomeId: string;
    };
    body: {
      data: NursingHomeUpdate;
    };
  }): Promise<NursingHome>;

  getLegalEntities(): Promise<{
    meta: {
      total_count: number;
    };
    data: LegalEntity[];
  }>;

  createLegalEntity(dataIn: {
    body: {
      data: {
        base_data: BaseData;
      };
    };
  }): Promise<{
    data: LegalEntity;
  }>;

  updateLegalEntity(dataIn: {
    params: {
      legalEntityId: string;
    };
    body: {
      data: {
        id: string;
        base_data: BaseData;
      };
    };
  }): Promise<{
    data: LegalEntity;
  }>;

  deleteLegalEntity(dataIn: {
    params: { legalEntityId: string };
  }): Promise<{ success: boolean }>;

  mangopayScaEnrollment(dataIn: {
    params: {
      legalEntityId: string;
    };
    body: {
      return_url: string;
    };
  }): Promise<{
    redirect_url: string;
  }>;
}

export class NursingHomePhoenixApi implements INursingHomePhoenixApi {
  baseUrl: string;
  baseUrlWithoutTenant: string;

  getTenantInformation: INursingHomePhoenixApi['getTenantInformation'];
  getLegalEntities: INursingHomePhoenixApi['getLegalEntities'];
  createLegalEntity: INursingHomePhoenixApi['createLegalEntity'];
  updateLegalEntity: INursingHomePhoenixApi['updateLegalEntity'];
  deleteLegalEntity: INursingHomePhoenixApi['deleteLegalEntity'];
  getNursingHomes: INursingHomePhoenixApi['getNursingHomes'];
  getNursingHome: INursingHomePhoenixApi['getNursingHome'];
  updateNursingHome: INursingHomePhoenixApi['updateNursingHome'];
  mangopayScaEnrollment: INursingHomePhoenixApi['mangopayScaEnrollment'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.baseUrlWithoutTenant = getPhoenixApiBaseUrl(apiUrl);

    this.getNursingHomes = get({
      authContext,
      url: `${this.baseUrl}/nursing-home`,
    });

    this.getNursingHome = get({
      authContext,
      url: ({ nursingHomeId }) =>
        `${this.baseUrl}/nursing-home/${nursingHomeId}`,
    });

    this.updateNursingHome = modify({
      authContext,
      url: ({ nursingHomeId }) =>
        `${this.baseUrl}/nursing-home/${nursingHomeId}`,
      method: 'PATCH',
    });

    this.getTenantInformation = get({
      authContext,
      url: `${this.baseUrlWithoutTenant}/api/tenant/${tenantId}`,
    });

    this.getLegalEntities = get({
      authContext,
      url: `${this.baseUrl}/legal-entity`,
    });

    this.createLegalEntity = modify({
      authContext,
      url: () => `${this.baseUrl}/legal-entity`,
      method: 'POST',
    });

    this.updateLegalEntity = modify({
      authContext,
      url: ({ legalEntityId }) =>
        `${this.baseUrl}/legal-entity/${legalEntityId}`,
      method: 'PATCH',
    });
    this.deleteLegalEntity = modify({
      method: 'DELETE',
      authContext,
      url: ({ legalEntityId }) =>
        `${this.baseUrl}/legal-entity/${legalEntityId}`,
    });

    this.mangopayScaEnrollment = modify({
      method: 'POST',
      authContext,
      url: ({ legalEntityId }) =>
        `${this.baseUrl}/legal-entity/${legalEntityId}/mangopay-sca-enrollment`,
    });
  }
}
